import {useCallback, useState} from 'react';
import {restoreReportVersionFromApi} from '../../../utils/api/reportVersions';
import {invalidateSnapshotCache} from '../../../utils/reportSnapshotCache';
import {useReportAccess} from '../reportPage/useReportAccess';
import {useSession} from '../../../firebase/UserProvider';
import {useReportStore} from '../useReportStore';

/**
 * Restore the live report to a historical preview version (creates a new latest version).
 *
 * @param {object} params
 * @param {string} params.reportId
 * @param {string|null|undefined} params.version - Currently selected preview version
 * @param {string|null|undefined} params.latestVersion
 * @param {boolean} [params.disabled]
 */
export function useRestoreVersion({reportId, version, latestVersion, disabled = false}) {
    const {user} = useSession() || {};
    const reportStatus = useReportStore((s) => s.status);
    const {role, isViewer, accessLoading} = useReportAccess(reportId, user);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [error, setError] = useState(null);

    const canEdit = (role === 'owner' || role === 'editor') && !isViewer;
    const isLatest = version && latestVersion && String(version) === String(latestVersion);
    const isApproved = reportStatus === 'approved';
    const canRestore = Boolean(
        canEdit && !accessLoading && reportId && version && !isLatest && !isApproved && !disabled
    );

    const openConfirm = useCallback(() => {
        setError(null);
        setConfirmOpen(true);
    }, []);

    const closeConfirm = useCallback(() => {
        if (!restoring) {
            setConfirmOpen(false);
            setError(null);
        }
    }, [restoring]);

    const handleRestore = useCallback(async () => {
        if (!reportId || !version) {
            return;
        }

        setRestoring(true);
        setError(null);
        try {
            const result = await restoreReportVersionFromApi(reportId, version);
            invalidateSnapshotCache(reportId);
            setConfirmOpen(false);
            const newVersion = result?.newVersion;
            const restoredQuery = newVersion != null ? `?restored=${newVersion}` : '';
            const cacheBust = `${restoredQuery ? '&' : '?'}t=${Date.now()}`;
            window.location.replace(`/products/${reportId}/edit${restoredQuery}${cacheBust}`);
        } catch (err) {
            console.error('[useRestoreVersion] restore failed:', err);
            setError(err?.message || 'Failed to restore version');
        } finally {
            setRestoring(false);
        }
    }, [reportId, version]);

    return {
        canRestore,
        confirmOpen,
        restoring,
        error,
        openConfirm,
        closeConfirm,
        handleRestore,
    };
}
